import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import api from '../services/api'
import Reveal from '../components/landing/Reveal'
import LoadingSpinner from '../components/LoadingSpinner'
import {
  ArrowRight,
  ArrowLeft,
  Clock,
  Award,
  CheckCircle2,
  Sparkles,
  Calendar,
  BookOpen,
  Zap,
  AlertCircle,
} from 'lucide-react'

export default function HolidayProgramsPage() {
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    document.title = 'Holiday Creative Programs | Synergy Multimedia Academy'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Make your holiday count with 6-week practical creative programs at Synergy Multimedia Academy. Photography, videography, graphic design, AI, animation and more. Real projects, real skills.'
      )
    }
  }, [])

  useEffect(() => {
    const fetchHolidayOfferings = async () => {
      try {
        const { data } = await api.get('/auth/holiday-offerings')
        setCourses(data?.courses || [])
      } catch (err) {
        console.error('[HolidayPrograms] Failed to load:', err)
        setError(err.response?.data?.error || 'Unable to load holiday programs. Please try again.')
      } finally {
        setLoading(false)
      }
    }
    fetchHolidayOfferings()
  }, [])

  return (
    <div className="cinema-page min-h-screen selection:bg-cinema-glow/30 selection:text-white">
      <Helmet>
        <title>Holiday Creative Programs — 6-Week Practical Courses | SMA Kampala</title>
        <meta
          name="description"
          content="Make your holiday count with 6-week practical creative programs at Synergy Multimedia Academy. Photography, videography, graphic design, AI, animation and more. Real projects, real skills."
        />
        <link rel="canonical" href="https://synergymultimediaacademy.com/holiday-programs" />
        <meta property="og:title" content="Holiday Creative Programs — Make Your Holiday Count | SMA" />
        <meta
          property="og:description"
          content="6-week practical creative programs. Turn your holiday into a practical skill, build real projects and leave with something you can show."
        />
        <meta property="og:url" content="https://synergymultimediaacademy.com/holiday-programs" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="cinema-grain" aria-hidden="true" />

      {/* Top Navigation */}
      <nav className="cinema-glass-strong sticky top-0 z-40 border-b border-white/5">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="flex justify-between items-center h-14 sm:h-20">
            <Link
              to="/explore"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-2.5 sm:gap-3 group shrink-0"
            >
              <img
                src="/Logos/SMA LOGO  DARK MODE.png"
                alt="Synergy Multimedia Academy logo"
                className="h-8 sm:h-12 transition-transform duration-500 group-hover:scale-105"
              />
              <div>
                <p className="font-display text-xs sm:text-sm tracking-tight leading-tight text-white">
                  Synergy Multimedia
                </p>
                <p className="text-xs uppercase tracking-[0.25em] text-cinema-gold font-medium">
                  Academy
                </p>
              </div>
            </Link>

            <div className="flex items-center gap-2.5 sm:gap-6">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 sm:gap-2 text-cinema-soft hover:text-cinema-gold text-[0.7rem] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-sans font-medium transition-colors group"
              >
                <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                <span className="hidden xs:inline sm:inline">Home</span>
              </Link>
              <Link
                to="/programs"
                className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-cinema-muted hover:text-cinema-gold transition-colors font-sans"
              >
                <BookOpen size={14} />
                <span>All Programs</span>
              </Link>
              <Link
                to="/register"
                className="cinema-btn-primary !px-3.5 sm:!px-5 !py-2 sm:!py-2.5 !min-h-[38px] sm:!min-h-[42px] !text-xs"
              >
                Apply Now
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative pt-8 pb-10 sm:pt-16 sm:pb-20 overflow-hidden bg-gradient-to-b from-cinema-charcoal/80 via-cinema-black to-cinema-black border-b border-white/5">
        <div className="cinema-glow-orb w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] -top-20 sm:-top-40 right-4 sm:right-10 opacity-30 sm:opacity-40 pointer-events-none" />
        <div className="cinema-glow-orb cinema-glow-orb-blue w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bottom-0 -left-10 sm:-left-20 opacity-20 sm:opacity-30 pointer-events-none" />

        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 relative z-10">
          <Reveal className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cinema-gold/10 border border-cinema-gold/30 text-cinema-gold text-xs uppercase tracking-[0.16em] font-sans font-semibold mb-4 sm:mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Holiday Creative Programs</span>
            </div>

            <h1 className="cinema-headline-xl text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-white font-light tracking-tight">
              MAKE YOUR <span className="text-cinema-gold">HOLIDAY</span> COUNT.
            </h1>

            <p className="mt-3 sm:mt-6 cinema-body-lg text-xs sm:text-sm md:text-base text-cinema-soft/80 leading-relaxed max-w-2xl">
              6-week practical programs designed for students, beginners and aspiring creatives.
              Turn your holiday into a practical skill, build real projects and leave with something you can show.
            </p>

            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5 sm:gap-4">
              <a
                href="#holiday-catalog"
                onClick={(e) => {
                  e.preventDefault()
                  document.getElementById('holiday-catalog')?.scrollIntoView({ behavior: 'smooth' })
                }}
                className="cinema-btn-primary !w-full sm:!w-auto !px-6 !py-3 !text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-2 shadow-lg shadow-cinema-gold/20 cursor-pointer min-h-[44px]"
              >
                <span>Explore Programs</span>
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/advisor"
                className="cinema-btn-ghost !w-full sm:!w-auto !px-5 !py-3 !text-xs uppercase tracking-wider font-semibold flex items-center justify-center gap-2 border border-white/20 hover:border-cinema-gold/60 cursor-pointer min-h-[44px]"
              >
                <span>Talk to Admissions</span>
              </Link>
            </div>
          </Reveal>

          {/* Quick Metrics Bar */}
          <Reveal delay={100} className="mt-8 sm:mt-10 grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-6 pt-6 sm:pt-8 border-t border-white/10">
            <div className="p-3 sm:p-4 rounded-sm bg-white/[0.02] border border-white/5">
              <div className="flex items-center gap-1.5 sm:gap-2 text-cinema-gold text-xs uppercase tracking-wider font-sans mb-1 font-semibold">
                <Clock className="w-4 h-4" />
                <span>Duration</span>
              </div>
              <p className="font-display text-xl sm:text-2xl md:text-3xl text-white font-light">6 Weeks</p>
              <p className="text-xs text-cinema-muted font-sans mt-0.5">Practical training</p>
            </div>
            <div className="p-3 sm:p-4 rounded-sm bg-white/[0.02] border border-white/5">
              <div className="flex items-center gap-1.5 sm:gap-2 text-cinema-gold text-xs uppercase tracking-wider font-sans mb-1 font-semibold">
                <Zap className="w-4 h-4" />
                <span>Approach</span>
              </div>
              <p className="font-display text-xl sm:text-2xl md:text-3xl text-white font-light">Real Projects</p>
              <p className="text-xs text-cinema-muted font-sans mt-0.5">Hands-on studio</p>
            </div>
            <div className="p-3 sm:p-4 rounded-sm bg-white/[0.02] border border-white/5">
              <div className="flex items-center gap-1.5 sm:gap-2 text-cinema-gold text-xs uppercase tracking-wider font-sans mb-1 font-semibold">
                <Award className="w-4 h-4" />
                <span>Outcome</span>
              </div>
              <p className="font-display text-xl sm:text-2xl md:text-3xl text-white font-light">Certificate</p>
              <p className="text-xs text-cinema-muted font-sans mt-0.5">Verifiable</p>
            </div>
            <div className="p-3 sm:p-4 rounded-sm bg-white/[0.02] border border-white/5">
              <div className="flex items-center gap-1.5 sm:gap-2 text-cinema-gold text-xs uppercase tracking-wider font-sans mb-1 font-semibold">
                <BookOpen className="w-4 h-4" />
                <span>Level</span>
              </div>
              <p className="font-display text-xl sm:text-2xl md:text-3xl text-white font-light">Beginner+</p>
              <p className="text-xs text-cinema-muted font-sans mt-0.5">No experience needed</p>
            </div>
          </Reveal>
        </div>
      </header>

      {/* Program Catalog */}
      <main id="holiday-catalog" className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-8 sm:py-16 scroll-mt-16 sm:scroll-mt-20">
        {loading ? (
          <div className="flex justify-center py-20">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="max-w-lg mx-auto text-center py-16">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-display text-white mb-2">Unable to Load Programs</h2>
            <p className="text-sm text-cinema-muted mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="cinema-btn-primary !px-6 !py-2.5 !text-xs"
            >
              Try Again
            </button>
          </div>
        ) : courses.length === 0 ? (
          <div className="max-w-lg mx-auto text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cinema-gold/10 to-cinema-gold/20 opacity-40" />
              <div className="relative w-full h-full flex items-center justify-center">
                <Calendar size={40} className="text-cinema-gold" strokeWidth={1.5} />
              </div>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Holiday Programs Coming Soon</h3>
            <p className="text-sm text-cinema-muted/70 mb-6 max-w-md mx-auto">
              Holiday Programs are currently being prepared. Check back soon for the next intake.
            </p>
            <Link to="/advisor" className="cinema-btn-ghost inline-flex items-center gap-2">
              <Sparkles size={16} /> Get Notified
            </Link>
          </div>
        ) : (
          <>
            <Reveal className="mb-6 sm:mb-10">
              <h2 className="font-display text-xl sm:text-2xl text-white font-light">
                Available <span className="text-cinema-gold">Holiday Programs</span>
              </h2>
              <p className="text-xs sm:text-sm text-cinema-muted mt-2 font-sans">
                {courses.filter((c) => c.is_registerable).length} program{courses.filter((c) => c.is_registerable).length !== 1 ? 's' : ''} currently open for registration · {courses.length} total
              </p>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {courses.map((course, index) => (
                <Reveal key={course.id} delay={index * 50}>
                  <HolidayProgramCard course={course} />
                </Reveal>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  )
}

function HolidayProgramCard({ course }) {
  const fmtUGX = (n) => 'UGX ' + (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })
  const fmtDate = (d) => {
    if (!d) return null
    try {
      return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    } catch {
      return String(d).slice(0, 10)
    }
  }
  const fmtDateLong = (d) => {
    if (!d) return null
    try {
      return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    } catch {
      return String(d).slice(0, 10)
    }
  }

  const detailPath = `/holiday-programs/${course.course_code}`

  // ─── Availability logic ──────────────────────────────────────────────
  // The backend sorts offerings so OPEN/CLOSING_SOON come first, then
  // UPCOMING (future registration_open_date), then DRAFT. We pick the best
  // offering to drive the card's availability display.
  const offerings = course.offerings || []
  const openOffering = offerings.find(
    (o) => o.computed_status === 'OPEN' || o.computed_status === 'CLOSING_SOON'
  )
  const upcomingOffering = !openOffering
    ? offerings.find(
        (o) =>
          o.computed_status === 'UPCOMING' ||
          Boolean(o.registration_open_date || o.start_date)
      )
    : null
  const bestOffering = openOffering || upcomingOffering || offerings[0] || null

  const hasScheduledDates = Boolean(
    bestOffering &&
      (bestOffering.registration_open_date ||
        bestOffering.registration_close_date ||
        bestOffering.start_date)
  )

  // Display states:
  //   'open'     → registration is live, show closing date + Register Now
  //   'upcoming' → intake dates / registration window set, show open date & intake schedule
  //   'fallback' → no scheduled dates yet, neutral TBA message
  const availabilityState = openOffering
    ? 'open'
    : hasScheduledDates
      ? 'upcoming'
      : 'fallback'

  const registerPath = openOffering
    ? `/register?course=${encodeURIComponent(course.course_code)}&program_type=HOLIDAY&intake_id=${openOffering.intake_id || ''}`
    : null

  return (
    <div className="group flex flex-col h-full bg-white/[0.02] border border-white/5 hover:border-cinema-gold/30 transition-all duration-500 rounded-sm overflow-hidden">
      {/* Badge row */}
      <div className="px-4 sm:px-5 pt-4 sm:pt-5 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-cinema-gold/10 border border-cinema-gold/30 text-cinema-gold text-[0.65rem] uppercase tracking-[0.12em] font-sans font-semibold">
          <Sparkles className="w-3 h-3" />
          Holiday
        </span>
        {availabilityState === 'open' ? (
          <span className="inline-flex items-center gap-1 text-[0.65rem] text-green-400 font-sans font-medium uppercase tracking-wider">
            <CheckCircle2 className="w-3 h-3" />
            Registration Open
          </span>
        ) : availabilityState === 'upcoming' ? (
          <span className="inline-flex items-center gap-1 text-[0.65rem] text-cinema-gold font-sans font-medium uppercase tracking-wider">
            <Calendar className="w-3 h-3" />
            {bestOffering?.registration_open_date
              ? `Opens ${fmtDateLong(bestOffering.registration_open_date)}`
              : bestOffering?.start_date
                ? `Intake: ${fmtDate(bestOffering.start_date)}`
                : 'Upcoming Intake'}
          </span>
        ) : (
          <span className="text-[0.65rem] text-cinema-muted font-sans font-medium uppercase tracking-wider">
            Dates TBA
          </span>
        )}
      </div>

      {/* Content */}
      <div className="px-4 sm:px-5 py-4 sm:py-5 flex-1 flex flex-col">
        <h3 className="font-display text-base sm:text-lg text-white font-medium leading-tight">
          {course.title}
        </h3>
        <p className="text-[0.7rem] text-cinema-muted font-mono mt-1 uppercase tracking-wider">
          {course.course_code}
        </p>

        {course.description && (
          <p className="text-xs text-cinema-soft/70 font-sans mt-3 leading-relaxed line-clamp-3 flex-1">
            {course.description}
          </p>
        )}

        {/* Meta row */}
        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-cinema-muted font-sans">
          {course.duration && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-cinema-gold/60" />
              {course.duration}
            </span>
          )}
          {course.level && (
            <span className="flex items-center gap-1">
              <BookOpen className="w-3 h-3 text-cinema-gold/60" />
              {course.level}
            </span>
          )}
        </div>

        {/* Fee */}
        {course.fee != null && (
          <p className="mt-3 font-display text-lg text-cinema-gold font-semibold">
            {fmtUGX(course.fee)}
          </p>
        )}

        {/* Availability / next intake info */}
        {bestOffering && hasScheduledDates && (
          <div className="mt-3 pt-3 border-t border-white/5 text-xs text-cinema-soft/80 font-sans space-y-1">
            {availabilityState === 'open' && (
              <>
                <span className="flex items-center gap-1.5 text-green-400/90 font-medium">
                  <Calendar className="w-3.5 h-3.5" />
                  Closes {fmtDateLong(openOffering.registration_close_date)}
                </span>
                {openOffering.start_date && (
                  <p className="pl-5 text-cinema-muted">
                    Program: {fmtDate(openOffering.start_date)} – {fmtDate(openOffering.end_date)}
                  </p>
                )}
              </>
            )}
            {availabilityState === 'upcoming' && (
              <>
                {bestOffering.registration_open_date && (
                  <span className="flex items-center gap-1.5 text-cinema-gold/90 font-medium">
                    <Calendar className="w-3.5 h-3.5 text-cinema-gold" />
                    {bestOffering.registration_close_date
                      ? `Registration: ${fmtDate(bestOffering.registration_open_date)} – ${fmtDate(bestOffering.registration_close_date)}`
                      : `Registration opens ${fmtDateLong(bestOffering.registration_open_date)}`}
                  </span>
                )}
                {bestOffering.start_date && (
                  <p className="pl-5 text-cinema-muted">
                    Program: {fmtDate(bestOffering.start_date)} – {fmtDate(bestOffering.end_date)}
                  </p>
                )}
                {bestOffering.intake_name && !bestOffering.registration_open_date && (
                  <p className="pl-5 text-cinema-muted">
                    Intake: {bestOffering.intake_name}
                  </p>
                )}
              </>
            )}
          </div>
        )}
        {availabilityState === 'fallback' && (
          <div className="mt-3 pt-3 border-t border-white/5 text-xs text-cinema-muted/60 font-sans">
            Next intake dates to be announced
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="px-4 sm:px-5 pb-4 sm:pb-5 flex gap-2">
        <Link
          to={detailPath}
          className="flex-1 cinema-btn-ghost !text-xs !py-2.5 flex items-center justify-center gap-1.5"
        >
          View Program
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
        {availabilityState === 'open' ? (
          <Link
            to={registerPath}
            className="flex-1 cinema-btn-primary !text-xs !py-2.5 flex items-center justify-center gap-1.5"
          >
            Register Now
          </Link>
        ) : availabilityState === 'upcoming' ? (
          <Link
            to={detailPath}
            className="flex-1 text-center text-xs text-cinema-gold/90 font-sans font-medium py-2.5 bg-cinema-gold/10 hover:bg-cinema-gold/20 border border-cinema-gold/30 rounded-sm transition-colors flex items-center justify-center gap-1"
          >
            {bestOffering?.registration_open_date
              ? `Opens ${fmtDate(bestOffering.registration_open_date)}`
              : bestOffering?.start_date
                ? `Starts ${fmtDate(bestOffering.start_date)}`
                : 'View Dates'}
          </Link>
        ) : (
          <span className="flex-1 text-center text-xs text-cinema-muted/60 font-sans py-2.5 border border-white/5 rounded-sm">
            Dates TBA
          </span>
        )}
      </div>
    </div>
  )
}
